import React from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { faRotateRight } from '@fortawesome/free-solid-svg-icons';
import MainButton from '../../../components/agv-button';
import EmptyList from '../../../components/agv-empty-list';
import GenericTableSkeleton from '../../../components/agv-generic-table/genericTableSkeleton';
import {
  canBeManuallyRetried,
  formatAttempts,
  formatDateTime,
  getLastErrorMessage,
  getLogStateDefinition,
  getLogStateLabel,
  LOG_STATE,
  getLogState,
} from '../logs.utils';

const SCROLL_CONTAINER_ID = 'tour-logs-scroll-container';
// Nombre maximum d'envois relancés en une fois (identique à l'API)
export const BULK_RETRY_MAX_COUNT = 100;

const isRetryable = canBeManuallyRetried;

/**
 * Liste des envois. L'état est une pastille de couleur (mêmes couleurs que les compteurs, libellé au survol).
 * En mode compact (panneau de détail ouvert), seules les colonnes essentielles restent.
 * Avec selectable (admins), les envois en action requise peuvent être sélectionnés pour une relance groupée.
 */
export default function LogsTable({
  logs,
  isFirstLoading,
  isRefreshing,
  hasMore,
  fetchNextPage,
  nextPageError,
  retryNextPage,
  selectedLogId,
  onSelectLog,
  compact,
  selectable,
  selectedIds,
  onSelectedIdsChange,
}) {
  const columns = compact
    ? 'tw-grid-cols-[20px_minmax(0,1fr)_70px]'
    : 'tw-grid-cols-[20px_minmax(0,1fr)_70px_minmax(0,2fr)_150px]';
  const rowLayout = selectable
    ? 'tw-grid tw-grid-cols-[36px_minmax(0,1fr)] tw-items-center'
    : 'tw-grid tw-grid-cols-[minmax(0,1fr)] tw-items-center';

  const retryableIds = (logs?.items || [])
    .filter(isRetryable)
    .slice(0, BULK_RETRY_MAX_COUNT)
    .map((log) => log.id);
  const allRetryableSelected =
    retryableIds.length > 0 && retryableIds.every((id) => selectedIds?.has(id));

  const toggleLog = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) {
      next.delete(id);
    } else if (next.size < BULK_RETRY_MAX_COUNT) {
      next.add(id);
    }
    onSelectedIdsChange(next);
  };

  const toggleAll = () => {
    onSelectedIdsChange(
      allRetryableSelected ? new Set() : new Set(retryableIds)
    );
  };

  return (
    <div className="tw-flex tw-flex-col tw-min-w-0 tw-flex-1 tw-bg-white tw-rounded-md tw-border tw-border-solid tw-border-black/10 tw-overflow-hidden">
      <div
        className={`${rowLayout} tw-bg-[#F7F8FA] tw-border-0 tw-border-b tw-border-solid tw-border-black/10 tw-text-xs tw-font-semibold tw-font-work tw-text-text-dark-medium-importance`}
        role="row"
      >
        {selectable && (
          <span className="tw-flex tw-justify-center">
            <input
              type="checkbox"
              checked={allRetryableSelected}
              onChange={toggleAll}
              disabled={!retryableIds.length}
              aria-label={`Sélectionner les envois en action requise affichés (${BULK_RETRY_MAX_COUNT} au plus)`}
              title={`Sélectionner les envois en action requise affichés (${BULK_RETRY_MAX_COUNT} au plus)`}
            />
          </span>
        )}
        <div className={`tw-grid ${columns} tw-gap-3 tw-px-4 tw-py-2`}>
          <span>
            <span className="tw-sr-only">État</span>
          </span>
          <span>Tournée</span>
          <span>Essais</span>
          {!compact && <span>Dernier message</span>}
          {!compact && <span>Dernière mise à jour</span>}
        </div>
      </div>

      <div
        id={SCROLL_CONTAINER_ID}
        aria-busy={isRefreshing}
        className={`tw-overflow-y-auto tw-transition-opacity ${
          isRefreshing ? 'tw-opacity-50 tw-pointer-events-none' : ''
        }`}
        style={{ height: 'calc(100vh - 330px)', minHeight: '360px' }}
      >
        {isFirstLoading ? (
          <GenericTableSkeleton />
        ) : !logs?.items?.length ? (
          <EmptyList />
        ) : (
          <InfiniteScroll
            dataLength={logs.items.length}
            next={fetchNextPage}
            hasMore={hasMore}
            loader={<GenericTableSkeleton numberOfRows={1} />}
            scrollableTarget={SCROLL_CONTAINER_ID}
          >
            {logs.items.map((log) => {
              const definition = getLogStateDefinition(log);
              const isSelected = selectedLogId === log.id;
              const isChecked = Boolean(selectedIds?.has(log.id));
              const errorMessage =
                log.status === 'ERRORED' ? getLastErrorMessage(log) : null;
              const showAttempts = getLogState(log) !== LOG_STATE.DONE;

              return (
                <div
                  key={log.id}
                  className={`${rowLayout} tw-border-0 tw-border-b tw-border-solid tw-border-black/5 ${
                    isSelected || isChecked
                      ? 'tw-bg-main/10'
                      : 'tw-bg-white hover:tw-bg-[#F7F8FA]'
                  }`}
                >
                  {selectable && (
                    <span className="tw-flex tw-justify-center">
                      {isRetryable(log) && (
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => toggleLog(log.id)}
                          disabled={
                            !isChecked &&
                            selectedIds?.size >= BULK_RETRY_MAX_COUNT
                          }
                          aria-label={`Sélectionner la tournée ${log.tour_key}`}
                        />
                      )}
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => onSelectLog(log)}
                    aria-current={isSelected ? 'true' : undefined}
                    className={`tw-grid ${columns} tw-gap-3 tw-items-center tw-w-full tw-px-4 tw-py-3 tw-text-left tw-text-sm tw-bg-transparent tw-border-0 focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-main`}
                  >
                    <span
                      role="img"
                      aria-label={getLogStateLabel(log)}
                      title={getLogStateLabel(log)}
                      className={`tw-inline-block tw-size-2.5 tw-rounded-full ${definition?.color}`}
                    />
                    <span className="tw-flex tw-flex-col tw-min-w-0">
                      <span className="tw-font-medium tw-truncate">
                        {log.tour_key}
                      </span>
                      {compact && errorMessage && (
                        <span
                          className="tw-text-xs tw-text-text-dark-medium-importance tw-truncate"
                          title={errorMessage}
                        >
                          {errorMessage}
                        </span>
                      )}
                    </span>
                    <span className="tw-text-text-dark-medium-importance">
                      {showAttempts ? formatAttempts(log) : '—'}
                    </span>
                    {!compact && (
                      <span
                        className="tw-truncate tw-text-text-dark-medium-importance"
                        title={errorMessage || undefined}
                      >
                        {errorMessage || '—'}
                      </span>
                    )}
                    {!compact && (
                      <span className="tw-text-text-dark-medium-importance">
                        {formatDateTime(log.updated_at)}
                      </span>
                    )}
                  </button>
                </div>
              );
            })}
          </InfiniteScroll>
        )}
        {nextPageError && (
          <div
            role="alert"
            className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-px-4 tw-py-3 tw-bg-status-negative/5 tw-text-sm"
          >
            <span className="tw-text-status-negative">
              Impossible de charger la suite de la liste : {nextPageError}
            </span>
            <MainButton
              variant="secondary"
              icon={faRotateRight}
              text="Réessayer"
              onClick={retryNextPage}
            />
          </div>
        )}
      </div>
    </div>
  );
}
